/**
 * Buffers microphone audio while the scribe's speech connection is down, so
 * it can be transcribed after reconnecting. Audio stays in memory only — never
 * written to browser storage — and is discarded once converted.
 */
import { downsampleBuffer, floatTo16BitPCM, pcmToWav, PCM_SAMPLE_RATE } from "@/lib/audio-pcm";

/** Matches the backend's backfill cap; longer outages keep a gap marker for the rest. */
export const OUTAGE_MAX_SECONDS = 5 * 60;

export interface OutageClip {
  wav: Blob;
  seconds: number;
}

export interface OutageRecorder {
  /** Stops buffering and returns the clip as 16 kHz mono WAV, or null if nothing usable. */
  stop: () => Promise<OutageClip | null>;
}

export function startOutageRecorder(stream: MediaStream, maxSeconds = OUTAGE_MAX_SECONDS): OutageRecorder | null {
  if (typeof MediaRecorder === "undefined" || !stream.getAudioTracks().some((t) => t.readyState === "live")) {
    return null;
  }
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream);
  } catch {
    return null;
  }
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });
  const capTimer = window.setTimeout(() => {
    if (recorder.state !== "inactive") recorder.stop();
  }, maxSeconds * 1000);
  recorder.start(1000);

  return {
    stop: async () => {
      window.clearTimeout(capTimer);
      if (recorder.state !== "inactive") recorder.stop();
      await stopped;
      if (!chunks.length) return null;
      const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
      chunks.length = 0;
      const audioContext = new AudioContext();
      try {
        const decoded = await audioContext.decodeAudioData(await blob.arrayBuffer());
        const pcm = floatTo16BitPCM(downsampleBuffer(decoded.getChannelData(0), decoded.sampleRate, PCM_SAMPLE_RATE));
        return { wav: pcmToWav(pcm), seconds: decoded.duration };
      } catch {
        return null;
      } finally {
        void audioContext.close();
      }
    },
  };
}
